/**
 * Per-page help registry.
 *
 * Each entry maps a URL pattern (exact path or prefix) to a small block
 * of help content the user sees when they tap the `?` icon in the top
 * bar. New screens self-register here — keep entries punchy (a one-line
 * intro + 3–6 bullets), and link out to the docs for anything longer.
 *
 * Resolution order in `resolveHelp(pathname)`:
 *   1. Exact path match
 *   2. Longest prefix match (so `/legionella/sites` falls back to
 *      `/legionella` if nothing more specific exists)
 *   3. The "_default" entry as a last resort
 */

export interface HelpEntry {
  title: string;
  intro: string;
  bullets?: string[];
  /** Optional "Read more" URL (external doc) */
  docs?: string;
  /** Optional related routes the user might want to jump to */
  related?: Array<{ label: string; to: string }>;
}

export const HELP: Record<string, HelpEntry> = {
  _default: {
    title: "Help",
    intro:
      "There isn't a tailored guide for this screen yet. Use the search (Cmd+K) to find what you're looking for, or contact support if you're stuck.",
  },

  "/": {
    title: "Dashboard",
    intro:
      "Your at-a-glance view of what's happening across every module — open tickets, water-compliance status, fleet activity, recent alerts.",
    bullets: [
      "The cards along the top show counts that update in near real time.",
      "Click any card to drill into the underlying screen.",
      "If you have access to multiple tenants, switch between them from the top-right tenant menu.",
    ],
    related: [
      { label: "Water Compliance", to: "/legionella" },
      { label: "Reports",          to: "/reports"   },
    ],
  },

  // ── Water Compliance ───────────────────────────────────────────
  "/legionella": {
    title: "Water Compliance",
    intro:
      "The hub for all water-safety work — clients, sites, locations, assets, monitoring schedules and the action reports an engineer files on each visit.",
    bullets: [
      "Pick a Client + Site at the top of every screen to scope what you see.",
      "Owners and admins get the extra ★ All sites option in the picker for client-wide views.",
      "Use the Asset types screen (under Legionella settings) to set the check-points and TMV rules each piece of equipment is monitored against.",
    ],
    related: [
      { label: "Sites",                to: "/legionella/sites" },
      { label: "Assets",               to: "/legionella/assets" },
      { label: "Action reports",       to: "/legionella/site-action-reports" },
      { label: "Legionella settings",  to: "/legionella/settings" },
    ],
  },

  "/legionella/clients": {
    title: "Clients",
    intro:
      "A client is whoever owns the buildings you maintain — a school, council, hospital trust, property manager. Sites belong to clients.",
    bullets: [
      "Use the search bar to find clients by name, contact or postcode.",
      "Per-page selector at the bottom of the table — your choice persists.",
      "Click a client to see their sites and edit the contact details that go on PDF reports.",
    ],
  },

  "/legionella/sites": {
    title: "Sites",
    intro:
      "A site is a single building. Locations and assets live underneath it, and every reading or report is keyed by site.",
    bullets: [
      "The pill next to the search bar shows the active client filter.",
      "The Locations column is a quick health-check — empty sites show up here.",
      "Click ▷ to drill into the locations at a site.",
    ],
  },

  "/legionella/locations": {
    title: "Locations",
    intro:
      "Locations are buildings, floors, rooms, or service areas inside a site. Assets attach to locations.",
    bullets: [
      "Search and per-page selector both work server-side, so the page is responsive on large tenants.",
      "Use the type filter (building/floor/room/area) to narrow things down.",
      "On a tenant with the All-sites picker active, this shows every location across the active client.",
    ],
  },

  "/legionella/assets": {
    title: "Assets",
    intro:
      "The TMVs, calorifiers, outlets, sentinel taps and other plumbing fixtures you monitor.",
    bullets: [
      "Filter by location using the dropdown next to the search bar — handy on big sites.",
      "Click an asset to see its full history (readings, NCs, programme membership).",
      "Asset Types (under Legionella settings) define the check-points that show up when you log a reading.",
    ],
  },

  "/legionella/work-orders": {
    title: "Work orders",
    intro:
      "Engineer-facing jobs — generated from programmes, raised manually, or triggered by an automatic non-conformity.",
    bullets: [
      "Status filter narrows to open / in-progress / completed jobs.",
      "Each WO has a per-asset breakdown — log all the readings for one asset with a single bulk modal.",
      "Engineers complete work orders in the mobile app; the web list is for managers to assign and review.",
    ],
  },

  "/legionella/risk-assessments": {
    title: "Risk assessments",
    intro:
      "L8/HSG274 risk assessments — the periodic deep audit of a site's water systems.",
    bullets: [
      "Each assessment is a series of questions per system (calorifier, cistern, mains, etc.).",
      "Answers are scored, summed into an overall risk level, and roll up into the actions register.",
      "Site action reports record what was done about findings between RAs.",
    ],
  },

  "/legionella/non-conformities": {
    title: "Non-conformities",
    intro:
      "Compliance issues raised against an asset — failed temperature readings, RA findings, or anything an engineer flags.",
    bullets: [
      "Auto-generated NCs come from temperature records that fall outside the compliance band for the asset's location point.",
      "Actions are recorded against the NC; the Closed status requires a corrective-action note.",
      "Risk level (low / medium / high / critical) determines SLA and escalation rules.",
    ],
  },

  "/legionella/site-action-reports": {
    title: "Site action reports",
    intro:
      "The digital triplicate of the engineer's site visit log. Customer signs on the mobile device; submission emails the customer, the site contact, and the company compliance file.",
    bullets: [
      "New reports are created in the engineer's mobile app — there's no Create button on the web on purpose.",
      "From here you can download any submitted report's PDF or re-send the emails (use the Send icon).",
      "Customers without a site contact email skip that copy automatically; the audit trail records who got what.",
    ],
  },

  // ── Vehicles ──────────────────────────────────────────────────
  "/vehicles": {
    title: "Vehicle tracking",
    intro:
      "Real-time GPS for the engineer fleet, plus trip history, driver assignments, geofences, fuel and maintenance.",
    bullets: [
      "Speed shown on the map is GPS-reported only — gaps in the trail mean the device lost signal, never an estimated dead-reckoning fix.",
      "Add a vehicle, then assign a tracker (the engineer's mobile app or a hardware unit) to start streaming.",
      "Geofences fire alerts when a vehicle enters/leaves a zone — handy for site arrival / depot-only rules.",
    ],
  },

  // ── People ────────────────────────────────────────────────────
  "/customers": {
    title: "Portal customers",
    intro:
      "Manage the customer-portal accounts your clients use to log in and see their own compliance data.",
    bullets: [
      "Each portal user is linked to one or more clients — they only see those clients' sites.",
      "The Features tab gates which sections of the portal they can access (live tracking, RAs, reports, etc.).",
      "Resend invite if a customer hasn't activated their account yet.",
    ],
  },

  // ── Reporting ────────────────────────────────────────────────
  "/reports": {
    title: "Reports",
    intro:
      "Build CSVs from the live data — temperature records, non-conformities, work orders, vehicle trips. Pre-built dashboards show top-level stats.",
    bullets: [
      "Pick a data source, set filters (date range, asset, site), preview the table, then Download CSV.",
      "Dates render in your local format. The CSV exports raw ISO dates for spreadsheet imports.",
      "For recurring reports emailed as a CSV (weekly digests etc.) use the Scheduled tab.",
    ],
  },

  // ── Admin ────────────────────────────────────────────────────
  "/admin": {
    title: "Admin panel",
    intro:
      "Tenant-level configuration: users + roles, integrations (Slack, Twilio, webhooks), security policy (IP allow-list, session timeout, password rotation), email templates, audit log.",
    bullets: [
      "The Security tab is where you turn on 2FA enforcement and configure the IP allow-list.",
      "Email templates lets you override the system templates (welcome, NC alert, etc.) per-tenant.",
      "Audit log → Export CSV downloads the full event log for compliance review.",
    ],
  },

  "/legionella/settings": {
    title: "Legionella settings",
    intro:
      "The configuration that drives the operational screens — asset types, programmes, compliance bands.",
    bullets: [
      "Asset types: define the check-points (hot outlet / TMV / sentinel etc.) each piece of equipment is monitored at.",
      "Programmes: bundle a recurring set of monitoring checks at a site into a single executable work order.",
      "Compliance bands: per (asset type, location point) min/max temperatures that determine pass/fail.",
    ],
    related: [
      { label: "Asset types",        to: "/legionella/settings/asset-types" },
      { label: "Programmes",         to: "/legionella/settings/programmes" },
      { label: "Compliance bands",   to: "/legionella/settings/compliance-bands" },
    ],
  },

  "/pricing": {
    title: "Plans & pricing",
    intro:
      "Subscription management. The plan you're on determines which features appear in the sidebar and which API endpoints respond.",
    bullets: [
      "The badge on your current plan shows the active subscription.",
      "Switching is handled by your account manager — pick a target and we'll adjust the contract.",
      "Super admins can edit the plan catalogue from /super/plans.",
    ],
  },

  "/onboarding": {
    title: "Onboarding wizard",
    intro:
      "First-run setup — we'll walk you through picking a plan, adding your first client + site, and inviting any teammates. You can skip every step except the welcome and come back later.",
    bullets: [
      "Tenant admins are prompted automatically until you finish or skip.",
      "Re-trigger the wizard any time from this URL — useful for demoing the flow.",
      "Super admins can reset a tenant's onboarding flag via POST /v1/me/onboarding/restart.",
    ],
  },
};

/**
 * Returns the help entry that best matches the current pathname, or
 * the `_default` fallback. Uses longest-prefix matching so unrecognised
 * sub-paths inherit help from their parent route.
 */
export function resolveHelp(pathname: string): HelpEntry {
  if (HELP[pathname]) return HELP[pathname];

  const candidates = Object.keys(HELP)
    .filter((k) => k !== "_default" && pathname.startsWith(k))
    .sort((a, b) => b.length - a.length);

  return candidates[0] ? HELP[candidates[0]] : HELP._default;
}
